<template>
  <div v-if="store.selectedCard.calculator[props.section]" :class="`earning-calculator-section ${props.section} mt-3`">
    <h4 class="subheading-2 fz-sm mb-2" :id="`input-range-label-${props.section}`" v-html="store.selectedCard.calculator[props.section].heading"></h4>
    <div class="input-wrap d-flex position-relative mt-4" ref="rangeSliderWrapEl">
      <input type="range" v-model="rangeSlider" @input="onInput($event)" ref="rangeSliderEl" class="w-100" min="0" :max="props.rangeMax" :step="props.points ? 1 : props.rangeStep" :aria-labelledby="`input-range-label-${props.section}`">
    </div>
    <div class="values-wrap">
      <div class="text-input">
        <label class="fz-4xs no-wrap" :for="`input-${props.section}`">{{ textInputLabel }}</label>
        <div class="d-flex align-items-center">
          <span class="mr-0-5">{{ textInputSymbol }}</span>
          <input type="text" v-model="rangeSlider" @input="onInput($event)" @blur="onBlur($event)" :id="`input-${props.section}`">
        </div>
      </div>
      <table>
        <tr v-for="(item, index) in rows" :key="index">
          <td class="value no-wrap">
            <span :class="`plus plus-${index + 1}`">+ </span>
            <span class="points-value">{{ pointsValue(item.multiplier) }}</span>
          </td>
          <td class="value-label">
            <!-- TODO: fix how this "to do" copy is added -->
            <span v-if="upToCopy && (index === 1 || index === 2)">up to </span>
            <span class="bolder" v-html="`${item.multiplier}${rowValueLabel}`"></span>
            <span v-html="item.label"></span>
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUpdated, nextTick } from 'vue';
import { useStore } from '../store';
import { q, numberWithCommas } from '../utilities';

const props = defineProps({
  section: String,
  rangeMax: Number,
  rangeStep: Number,
  rangeResolution: Number,
  points: Boolean,
});

const store = useStore();

const rangeSlider = ref(0);
const rangeSliderWrapEl = ref(null);
const rangeSliderEl = ref(null);
const textInputLabel = ref(0);
const textInputSymbol = ref(0);
const rowValueLabel = ref(0);

function drawRangeSliderMarkers() {
  const markerAmount = props.rangeMax / props.rangeResolution + 1;

  // Remove existing markers in order to reset values
  if (rangeSliderWrapEl._rawValue && rangeSliderWrapEl._rawValue.querySelector('.marker')) {
    const markerElsNodeList = rangeSliderWrapEl._rawValue.querySelectorAll('.marker');
    const markerElsArray = Array.from(markerElsNodeList);
    markerElsArray.forEach(el => el.remove());
  }

  for (let i = 0; i < markerAmount; i++) {
    const leftAmount = ((i + 0.09) / (markerAmount - 1)) * 96.3;
    const markerEl = document.createElement('div');
    markerEl.classList.add('marker', `marker-${i + 1}`);
    markerEl.style.left = `${leftAmount}%`;

    const markerLabelEl = document.createElement('span');
    if (props.points) {
      markerLabelEl.textContent = `${i * props.rangeStep}`;
    } else {
      markerLabelEl.textContent = `$${i * props.rangeStep}K`;
    }
    markerEl.appendChild(markerLabelEl);

    if (rangeSliderWrapEl._rawValue) {
      rangeSliderWrapEl._rawValue.appendChild(markerEl);
    }
  }
}

const rows = computed(() => {
  if (store.selectedTier[props.section]) {
    return store.selectedTier[props.section].rows;
  } else {
    return store.selectedCard.calculator[props.section].rows;
  }
});

function pointsValue(multiplier) {
  if (Number.isInteger(Number(rangeSlider.value))) {
    if (rangeSlider.value > props.rangeMax) {
      rangeSlider.value = props.rangeMax;
    }
    if (props.points) {
      return numberWithCommas(Math.round(rangeSlider.value * 1000));
    } else {
      return numberWithCommas(Math.round(rangeSlider.value * multiplier));
    }
    // ELSE IF non integers are entered in inputs, return 0
  } else {
    return 0;
  }
};

const emit = defineEmits(['dollarsInput']);

function onInput(e) {
  // Keeps user from entering non-number characters by stripping them out
  rangeSlider.value = rangeSlider.value.toString().replace(/\D+/, '');
  nextTick(() => {
    if (e.target.value > props.rangeMax) {
      e.target.value = props.rangeMax;
    }
    if (e.target.value === '') {
      rangeSliderEl._rawValue.value = 0;
    }
  });
  emit('dollarsInput');
}

function onBlur(e) {
  if (e.target.value === '') {
    rangeSlider.value = 0;
  }
}

function setLabelText() {
  if (props.points) {
    textInputLabel.value = 'Enter number of stays';
    textInputSymbol.value = '#';
    rowValueLabel.value = 'K';
  } else {
    textInputLabel.value = 'Enter dollar value';
    textInputSymbol.value = '$';
    rowValueLabel.value = 'X';
  }
}

const upToCopy = computed(() => {
  if (
    store.selectedCard.version === 'bevy' ||
    store.selectedCard.version === 'business' ||
    store.selectedCard.version === 'brilliant' ||
    store.selectedCard.version === 'consumer'
  ) {
    return true;
  } else {
    return false;
  }
});

onMounted(() => {
  drawRangeSliderMarkers();
  setLabelText();
});

onUpdated(() => {
  drawRangeSliderMarkers();
});
</script>
